'use client';

import { ILanguage } from 'my-poppies-b2b-types';
import React, { createContext, PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { de as deTranslations } from '@/i18n/de';
import { en as enTranslations } from '@/i18n/en';
import { ITranslationKeys, ITranslations } from '@/i18n/ITranslations';
import { ILanguageContextProps } from '@/types/ILanguageContextProps';

export const LanguageContext = createContext<ILanguageContextProps>({} as ILanguageContextProps);

const translationMapping: Record<ILanguage, ITranslations> = {
  de: deTranslations,
  en: enTranslations,
};

const availableLanguages: ILanguage[] = ['de', 'en'];

const languageFallback: ILanguage = 'en';

export const LanguageContextProvider = ({ children }: PropsWithChildren) => {
  const [currentLanguage, setCurrentLanguage] = useState<ILanguage | undefined>(undefined);

  const currentLanguageWithFallback = currentLanguage ?? languageFallback;

  const currentTranslations: ITranslations = translationMapping[currentLanguageWithFallback];

  const t = useCallback(
    (key: ITranslationKeys, count?: number, languageOverride?: ILanguage): string => {
      const translation = languageOverride ? translationMapping[languageOverride][key] : currentTranslations[key];
      if (translation !== '' && !translation) {
        throw new Error(`No translation found for key '${key}' in current language '${currentLanguageWithFallback}'`);
      }
      if (typeof translation !== 'string') {
        if (count === undefined) {
          throw new Error(`Retrieved translation for key '${key}' is a function, but no count value was provided`);
        } else {
          return translation[count === 1 ? 'singular' : 'plural'];
        }
      } else {
        if (count !== undefined) {
          throw new Error(`Retrieved translation for key '${key}' is a string, but a count value was provided`);
        } else {
          return translation;
        }
      }
    },
    [currentLanguageWithFallback, currentTranslations]
  );

  const setCurrentLanguageSafe = useCallback(
    (newDesiredLanguage: ILanguage) => {
      if (newDesiredLanguage && newDesiredLanguage !== currentLanguage) {
        setCurrentLanguage(newDesiredLanguage);
      }
    },
    [currentLanguage]
  );

  const value: ILanguageContextProps = useMemo((): ILanguageContextProps => {
    return {
      availableLanguages,
      t,
      currentLanguage,
      setCurrentLanguage: setCurrentLanguageSafe,
      currentLanguageWithFallback,
    };
  }, [t, currentLanguage, setCurrentLanguageSafe, currentLanguageWithFallback]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
